import React, { useEffect, useMemo, useState } from 'react';
import { searchCategories } from '../../utils/categorySearch';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { createApiClient, getApiBaseUrl } from '../../services/httpClient';
import useBusinessCategoryOptions from '../../hooks/useBusinessCategoryOptions';

const api = createApiClient(getApiBaseUrl());
const endpoint = '/franchisee/admin/assisted-registration';
const LOKSY_HOME_URL = 'https://loksy.in/';

const empty = {
  phone: '',
  business_name: '',
  contact_person_name: '',
  business_type: '',
  address: '',
  city: '',
  state: '',
  pincode: '',
};

function digitsOnly(value, maxLength) {
  return String(value || '')
    .replace(/\D/g, '')
    .slice(0, maxLength);
}

function buildWelcomeWhatsAppMessage(businessName) {
  const shop = String(businessName || '').trim();
  const greeting = shop
    ? `Hi! Welcome to Loksy Vyapar — ${shop}`
    : 'Hi! Welcome to Loksy Vyapar';
  return [
    greeting,
    '',
    'Your shop is now on Loksy. Reach nearby customers, share offers, and grow your local business.',
    '',
    `Open Loksy: ${LOKSY_HOME_URL}`,
    '',
    'నమస్కారం! Loksy Vyapar కి స్వాగతం.',
    'మీ వ్యాపారాన్ని సమీప కస్టమర్లకు చేరువ చేయండి — ఆఫర్లు పంచుకోండి, వ్యాపారం పెంచుకోండి.',
    LOKSY_HOME_URL,
    '',
    '— Team Loksy',
  ].join('\n');
}

function buildWhatsAppUrl(phone, message) {
  const cleanPhone = digitsOnly(phone, 10);
  if (cleanPhone.length !== 10) return null;
  return `https://wa.me/91${cleanPhone}?text=${encodeURIComponent(message)}`;
}

function WhatsAppWelcomeButton({ phone, businessName }) {
  const cleanPhone = digitsOnly(phone, 10);
  return (
    <Button
      type="button"
      variant="contained"
      startIcon={<WhatsAppIcon />}
      disabled={cleanPhone.length !== 10}
      onClick={() => {
        const url = buildWhatsAppUrl(
          cleanPhone,
          buildWelcomeWhatsAppMessage(businessName),
        );
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
      }}
      sx={{
        bgcolor: '#25D366',
        whiteSpace: 'nowrap',
        minHeight: 56,
        '&:hover': { bgcolor: '#1da851' },
      }}
    >
      WhatsApp
    </Button>
  );
}

function dedupeCategories(list) {
  const seen = new Set();
  const out = [];
  for (const o of list || []) {
    const key = String(o?.value || '').trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(o);
  }
  return out;
}

/** Strict substring filter on label/value — ignores MUI's fuzzy default. */
function filterCategoriesByQuery(opts, rawQuery) {
  return searchCategories(opts, rawQuery);
}

export default function AssistedRegistrationForm() {
  const { options: rawOptions, loading, error: categoryError } =
    useBusinessCategoryOptions();
  const options = useMemo(() => dedupeCategories(rawOptions), [rawOptions]);
  const [notice, setNotice] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [categoryInput, setCategoryInput] = useState('');
  const [whatsappSameAsPhone, setWhatsappSameAsPhone] = useState(true);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [phoneChecking, setPhoneChecking] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const selectedCategory = useMemo(
    () => options.find((o) => o.value === form.business_type) || null,
    [options, form.business_type],
  );
  const filteredCategoryOptions = useMemo(
    () => filterCategoriesByQuery(options, categoryInput),
    [options, categoryInput],
  );
  const effectiveWhatsappNumber = whatsappSameAsPhone
    ? form.phone
    : whatsappNumber;

  useEffect(() => {
    let live = true;
    api
      .get(endpoint)
      .then((r) => {
        if (live) setNotice(r.data.notice);
      })
      .catch((e) => {
        if (live) {
          setError(
            e.response?.data?.message ||
              'Unable to load registration permission/notice. Reload to retry.',
          );
        }
      });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    const phone = digitsOnly(form.phone, 10);
    if (phone.length !== 10) {
      setPhoneChecking(false);
      setPhoneError('');
      return undefined;
    }
    let live = true;
    setPhoneChecking(true);
    setPhoneError('');
    const timer = setTimeout(() => {
      api
        .post('/franchisee/public/check-phone', { phone })
        .then((r) => {
          if (!live) return;
          const data = r.data?.data || {};
          if (data.registered || data.draft) {
            setPhoneError(
              'This phone already has an account or draft. Use the existing recovery flow.',
            );
          } else {
            setPhoneError('');
          }
        })
        .catch(() => {
          if (live) setPhoneError('');
        })
        .finally(() => {
          if (live) setPhoneChecking(false);
        });
    }, 250);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [form.phone]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy || !notice || phoneChecking) return;
    if (phoneError) {
      setError(phoneError);
      return;
    }
    if (digitsOnly(form.phone, 10).length !== 10) {
      setError('Enter a valid 10-digit merchant phone.');
      return;
    }
    if (
      !whatsappSameAsPhone &&
      digitsOnly(whatsappNumber, 10).length !== 10
    ) {
      setError('Enter a valid 10-digit WhatsApp number, or choose Yes, same.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const r = await api.post(
        endpoint,
        { ...form, assisted_notice_version: notice.version },
        { timeout: 90000 },
      );
      setReceipt(r.data.data);
      setForm(empty);
      setCategoryInput('');
      setWhatsappSameAsPhone(true);
      setWhatsappNumber('');
      setPhoneError('');
    } catch (err) {
      setError(
        err.response?.data?.message ||
          'Response not received. Check the Mitra account list for this phone before retrying; the account may have been created.',
      );
    } finally {
      setBusy(false);
    }
  };

  if (receipt) {
    const shareUrl = buildWhatsAppUrl(receipt.phone, receipt.delivery_message);
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack spacing={2}>
          <Alert severity="success">
            Vyapar account created. Your admin login remains active.
          </Alert>
          <Typography>Receipt: {receipt.id}</Typography>
          <Typography>
            {receipt.business_name} — {receipt.phone}
          </Typography>
          <Alert severity="info">
            The merchant must complete mobile OTP after their first PIN login.
            This verifies their registered mobile; it does not grant a business Verified badge.
          </Alert>
          <TextField
            label="Merchant login details — share once"
            value={receipt.delivery_message || ''}
            multiline
            minRows={8}
            InputProps={{ readOnly: true }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            {shareUrl && (
              <Button
                variant="contained"
                color="success"
                startIcon={<WhatsAppIcon />}
                onClick={() => window.open(shareUrl, '_blank', 'noopener,noreferrer')}
              >
                Send credentials on WhatsApp
              </Button>
            )}
            {receipt.auto_login_url && (
              <Button href={receipt.auto_login_url} target="_blank" rel="noreferrer">
                Test auto-login link
              </Button>
            )}
            {receipt.play_store_url && (
              <Button href={receipt.play_store_url} target="_blank" rel="noreferrer">
                Install Loksy Mitra
              </Button>
            )}
          </Stack>
          <Typography>{receipt.access_disclaimer}</Typography>
          {receipt.trial_ends_at && (
            <Typography>
              Trial ends: {new Date(receipt.trial_ends_at).toLocaleDateString()}
            </Typography>
          )}
          <Typography>
            Registration recorded: {receipt.recorded_at} · {receipt.notice_version}
          </Typography>
          <Button onClick={() => window.print()}>Print receipt</Button>
          <Button onClick={() => setReceipt(null)}>Register another Vyapar</Button>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box component="form" onSubmit={submit}>
        <Stack spacing={2}>
          <Typography variant="h5">Register Vyapar — admin assistance</Typography>
          <Typography>
            Fallback when merchant OTP is unavailable. Use only with the
            merchant’s written authorization. Existing accounts must use account
            recovery.
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          {categoryError && <Alert severity="warning">{categoryError}</Alert>}
          <Box
            component="fieldset"
            disabled={busy || !notice}
            sx={{ border: 0, p: 0, m: 0 }}
          >
            <Stack spacing={2}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', sm: 'flex-start' }}
              >
                <TextField
                  fullWidth
                  label="Merchant phone (10 digits)"
                  value={form.phone}
                  required
                  autoComplete="off"
                  error={Boolean(phoneError)}
                  helperText={
                    phoneChecking
                      ? 'Checking if this number is already registered…'
                      : phoneError || undefined
                  }
                  inputProps={{
                    maxLength: 10,
                    pattern: '[0-9]{10}',
                    inputMode: 'numeric',
                  }}
                  onChange={(e) =>
                    setForm({ ...form, phone: digitsOnly(e.target.value, 10) })
                  }
                />
                {whatsappSameAsPhone && (
                  <WhatsAppWelcomeButton
                    phone={form.phone}
                    businessName={form.business_name}
                  />
                )}
              </Stack>
              <FormControl>
                <FormLabel id="whatsapp-same-label">
                  Is WhatsApp number the same as this phone?
                </FormLabel>
                <RadioGroup
                  row
                  aria-labelledby="whatsapp-same-label"
                  value={whatsappSameAsPhone ? 'yes' : 'no'}
                  onChange={(e) => {
                    const same = e.target.value === 'yes';
                    setWhatsappSameAsPhone(same);
                    if (same) setWhatsappNumber('');
                  }}
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio />}
                    label="Yes, same"
                  />
                  <FormControlLabel
                    value="no"
                    control={<Radio />}
                    label="No — enter WhatsApp number"
                  />
                </RadioGroup>
              </FormControl>
              {!whatsappSameAsPhone && (
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                >
                  <TextField
                    fullWidth
                    label="WhatsApp number (10 digits)"
                    value={whatsappNumber}
                    required
                    autoComplete="off"
                    helperText="Enter the merchant’s WhatsApp number to send the welcome message"
                    inputProps={{
                      maxLength: 10,
                      pattern: '[0-9]{10}',
                      inputMode: 'numeric',
                    }}
                    onChange={(e) =>
                      setWhatsappNumber(digitsOnly(e.target.value, 10))
                    }
                  />
                  <WhatsAppWelcomeButton
                    phone={effectiveWhatsappNumber}
                    businessName={form.business_name}
                  />
                </Stack>
              )}
              {Object.entries({
                business_name: 'Business name',
                contact_person_name: 'Merchant / authorized representative',
                address: 'Business address',
                city: 'Town / city',
                state: 'State',
                pincode: 'PIN code',
              }).map(([key, label]) => (
                <TextField
                  key={key}
                  label={label}
                  value={form[key]}
                  required={!['address', 'city', 'state', 'pincode'].includes(key)}
                  autoComplete="off"
                  inputProps={{
                    maxLength: key === 'pincode' ? 6 : 200,
                    ...(key === 'pincode'
                      ? { pattern: '[0-9]{6}', inputMode: 'numeric' }
                      : {}),
                  }}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      [key]: key === 'pincode' ? digitsOnly(e.target.value, 6) : e.target.value,
                    })
                  }
                />
              ))}
              <Autocomplete
                options={filteredCategoryOptions}
                loading={loading}
                disabled={loading}
                value={selectedCategory}
                inputValue={categoryInput}
                onInputChange={(_, text, reason) => {
                  if (reason === 'reset' && selectedCategory) {
                    setCategoryInput(selectedCategory.label || '');
                    return;
                  }
                  setCategoryInput(text);
                }}
                onChange={(_, opt) => {
                  setForm({ ...form, business_type: opt?.value || '' });
                  setCategoryInput(opt?.label || '');
                }}
                isOptionEqualToValue={(a, b) => a?.value === b?.value}
                getOptionLabel={(o) => o?.label || ''}
                filterOptions={(opts, state) =>
                  filterCategoriesByQuery(
                    opts,
                    state.inputValue || categoryInput,
                  )
                }
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.value}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {option.label}
                      {option.teluguName ? (
                        <Typography component="span" variant="caption" color="text.secondary">
                          {' '}· {option.teluguName}
                        </Typography>
                      ) : null}
                    </Typography>
                  </Box>
                )}
                autoHighlight
                clearOnBlur={false}
                handleHomeEndKeys
                noOptionsText={
                  categoryInput.trim()
                    ? `No match for “${categoryInput.trim()}” — try kirana, tailor, tea…`
                    : 'Type to find a category'
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Business category"
                    placeholder="Find category — type kirana, tailor, tea…"
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: 'off',
                    }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon color="action" fontSize="small" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <Alert severity="info">
                {notice?.text || 'Loading permission and notice…'}
              </Alert>
              <Typography>
                <a
                  href="https://services.knls.in/locsy-terms-and-conditions"
                  target="_blank"
                  rel="noreferrer"
                >
                  Terms
                </a>{' '}
                ·{' '}
                <a
                  href="https://services.knls.in/privacy-policy"
                  target="_blank"
                  rel="noreferrer"
                >
                  Privacy Policy
                </a>{' '}
                ·{' '}
                <a
                  href="https://services.knls.in/refund-policy"
                  target="_blank"
                  rel="noreferrer"
                >
                  Refund Policy
                </a>
              </Typography>

              <Button
                type="submit"
                variant="contained"
                disabled={
                  busy ||
                  !notice ||
                  loading ||
                  phoneChecking ||
                  Boolean(phoneError)
                }
              >
                {busy
                  ? 'Creating account…'
                  : 'Create Vyapar account'}
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Paper>
  );
}
